import { CarSearch } from '../../models/car-search.model';
import { restoreExecutedSearch, resetSearchHistorySession, searchViewportChanged } from '../../search-history/search-history.actions';
import { sharedModeFilters } from '../../search-history/search-history.model';
import { createReducer, on } from '@ngrx/store';
import { CarSearchResult } from '../../models/car-model';
import { mapSearchSnapshot, withoutViewport } from '../../models/search-request';
import { clearAuthUser, clearSessionState, logout } from '../actions/auth.actions';
import {
  addCar,
  addCarFailure,
  addCarReportSuccess,
  addCarSuccess,
  changeCarPage,
  changeCarPageSize,
  changeCarSort,
  clearCarPolygon,
  ensureCarList,
  getCarFailure,
  getCarSuccess,
  getTopCars,
  getTopCarsFailure,
  getTopCarsSuccess,
  likeCarFailure,
  likeCarSuccess,
  removeCarFailure,
  removeCarSuccess,
  resetCarSearch,
  retryCarSearch,
  searchCarReports,
  searchCarReportsFailure,
  searchCarReportsSuccess,
  searchCars,
  searchCarsFailure,
  searchCarsSuccess,
  searchMapCars,
  searchMapCarsFailure,
  searchMapCarsSuccess,
  setCarMapView,
  setCarPolygon,
  setCarSoldFailure,
  setCarSoldSuccess,
  toggleCarMapView,
  updateCar,
  updateCarFailure,
  updateCarReportFailure,
  updateCarReportSuccess,
  updateCarSuccess,
  updateSearchForm,
  upgradeCar,
} from '../actions/car.actions';
import { initialCarState } from '../states/car.state';

export const carReducer = createReducer(
  initialCarState,
  on(resetSearchHistorySession, state => ({ ...initialCarState, searchRestoreRevision: state.searchRestoreRevision + 1 })),
  on(searchViewportChanged, (state, action) => action.domain !== 'CAR' ? state : ({ ...state, mapViewport: action.viewport })),
  on(restoreExecutedSearch, (state, entry) => {
    if (entry.domain !== 'CAR') return state;
    const criteria = entry.criteria as unknown as CarSearch;
    const isMap = entry.mode === 'map';
    const shared = withoutViewport(criteria);
    return { ...state, historyRestored: true, searchRestoreRevision: state.searchRestoreRevision + 1,
      isCarMapView: isMap, query: isMap ? state.query : shared,
      listDraft: structuredClone(shared), mapDraft: structuredClone(shared),
      mapQuery: isMap ? structuredClone(criteria) : state.mapQuery,
      activePolygon: isMap ? criteria.polygonFilter ?? null : state.activePolygon,
      polygonRevision: state.polygonRevision + 1,
      mapViewport: isMap ? entry.viewport : state.mapViewport,
      listLoading: !isMap, mapLoading: isMap, listError: null, mapError: null,
      carResults: isMap ? state.carResults : undefined, carMapResults: isMap ? undefined : state.carMapResults,
      sort: isMap ? state.sort : { active: criteria.sort ?? null, direction: criteria.direction ?? null },
      mapSort: isMap ? { active: criteria.sort ?? null, direction: criteria.direction ?? null } : state.mapSort,
      pagination: {...state.pagination, pageIndex: 0, ...(!isMap && criteria.limit && criteria.limit > 0 ? {pageSize: criteria.limit} : {})},
      mapPagination: {...state.mapPagination, pageIndex: 0},
    };
  }),

  on(logout, clearAuthUser, clearSessionState, (state) => ({
    ...initialCarState,
    polygonRevision: state.polygonRevision + 1,
  })),
  on(resetCarSearch, (state) => ({
    ...state,
    ...(state.isCarMapView
      ? {
          mapQuery: undefined,
          mapDraft: undefined,
          carMapResults: undefined,
          activePolygon: null,
          polygonRevision: state.polygonRevision + 1,
          mapLoading: false,
          mapError: null,
          mapPagination: { pageIndex: 0, pageSize: 12 },
          mapSort: { active: null, direction: null },
        }
      : {
          query: undefined,
          listDraft: undefined,
          carResults: undefined,
          listLoading: false,
          listError: null,
          pagination: { pageIndex: 0, pageSize: 12 },
          sort: { active: null, direction: null },
        }),
  })),
  on(setCarPolygon, (state, { polygon }) => ({
    ...state,
    activePolygon: polygon ? polygon.map((p) => ({ ...p })) : null,
    mapQuery:
      (state.mapDraft ?? state.mapQuery ?? state.query)
        ? structuredClone(withoutViewport((state.mapDraft ?? state.mapQuery ?? state.query)!))
        : undefined,
    mapPagination: { ...state.mapPagination, pageIndex: 0 },
    mapLoading: !!polygon,
    mapError: null,
    carMapResults: undefined,
  })),

  on(searchCars, (state, { query }) => ({
    ...state,
    query: structuredClone(withoutViewport(query)),
    listDraft: structuredClone(withoutViewport(query)),
    pagination: { ...state.pagination, pageIndex: 0 },
    listLoading: true,
    listError: null,
    carResults: undefined,
  })),

  on(searchCarsSuccess, (state, { carResults }) => ({
    ...state,
    carResults,
    listLoading: false,
    listError: null,
    pagination: {
      ...state.pagination,
      pageIndex: carResults?.pageIndex ?? state.pagination.pageIndex,
    },
  })),

  on(searchCarsFailure, (state, { error }) => ({ ...state, listLoading: false, listError: error })),

  on(searchMapCars, (state, { mapQuery }) => ({
    ...state,
    mapQuery: mapSearchSnapshot(mapQuery, state.mapQuery),
    mapDraft: structuredClone(withoutViewport(mapQuery)),
    mapPagination: { ...state.mapPagination, pageIndex: 0 },
    mapLoading: true,
    mapError: null,
    carMapResults: undefined,
  })),

  on(searchMapCarsSuccess, (state, { carMapResults }) => ({
    ...state,
    carMapResults,
    mapLoading: false,
    mapError: null,
  })),

  on(searchMapCarsFailure, (state, { error }) => ({
    ...state,
    mapLoading: false,
    mapError: error,
  })),

  on(changeCarSort, (state, { active, direction }) => ({
    ...state,
    ...(state.isCarMapView
      ? { mapSort: { active, direction }, mapPagination: { ...state.mapPagination, pageIndex: 0 } }
      : {
          sort: { active, direction },
          pagination: { ...state.pagination, pageIndex: 0 },
          listLoading: true,
          listError: null,
          carResults: undefined,
        }),
  })),

  on(changeCarPage, (state, { pageIndex }) => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageIndex,
    },
  })),

  on(changeCarPageSize, (state, { pageSize }) => ({
    ...state,
    pagination: {
      pageSize,
      pageIndex: 0,
    },
  })),

  on(getTopCars, (state) => ({
    ...state,
    topLoading: true,
    error: null,
  })),

  on(getTopCarsSuccess, (state, { topCarResults: topCarResults }) => ({
    ...state,
    topCarResults: topCarResults,
    topLoading: false,
  })),

  on(getTopCarsFailure, (state, { error }) => ({
    ...state,
    topLoading: false,
    error,
  })),

  on(updateSearchForm, (state, { form }) => ({
    ...state,
    ...(state.isCarMapView
      ? { mapDraft: structuredClone(withoutViewport(form)) }
      : { listDraft: structuredClone(withoutViewport(form)) }),
  })),

  on(getCarSuccess, (state, { car }) => ({
    ...state,
    loading: false,
    car: car,
  })),

  on(getCarFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(addCar, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(addCarSuccess, (state, { car }) => ({
    ...state,
    isSaving: false,
    car: car,
    carResults: state.carResults
      ? { ...state.carResults, items: [...state.carResults.items, car] }
      : ({ items: [car], total: 1, pageIndex: 0, pageSize: 12, pages: [1] } as CarSearchResult),
  })),

  on(addCarFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(updateCar, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(upgradeCar, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),

  on(updateCarSuccess, (state, { car }) => ({
    ...state,
    isSaving: false,
    car: car,
    carResults: state.carResults
      ? {
          ...state.carResults,
          items: state.carResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carResults,

    topCarResults: state.topCarResults
      ? {
          ...state.topCarResults,
          items: state.topCarResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.topCarResults,

    carMapResults: state.carMapResults
      ? {
          ...state.carMapResults,
          items: state.carMapResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carMapResults,
  })),

  on(updateCarFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(likeCarSuccess, (state, { car }) => ({
    ...state,
    loading: false,
    car,
    carResults: state.carResults
      ? {
          ...state.carResults,
          items: state.carResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carResults,

    topCarResults: state.topCarResults
      ? {
          ...state.topCarResults,
          items: state.topCarResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.topCarResults,

    carMapResults: state.carMapResults
      ? {
          ...state.carMapResults,
          items: state.carMapResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carMapResults,
  })),
  on(likeCarFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(setCarSoldSuccess, (state, { car }) => ({
    ...state,
    isSaving: false,
    car: car,
    carResults: state.carResults
      ? {
          ...state.carResults,
          items: state.carResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carResults,

    topCarResults: state.topCarResults
      ? {
          ...state.topCarResults,
          items: state.topCarResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.topCarResults,

    carMapResults: state.carMapResults
      ? {
          ...state.carMapResults,
          items: state.carMapResults.items.map((c) => (c.id === car.id ? car : c)),
        }
      : state.carMapResults,
  })),

  on(setCarSoldFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(removeCarSuccess, (state, { id }) => ({
    ...state,
    isSaving: false,
    carResults: state.carResults
      ? {
          ...state.carResults,
          items: state.carResults.items.filter((car) => car.id !== id),
        }
      : state.carResults,

    topCarResults: state.topCarResults
      ? {
          ...state.topCarResults,
          items: state.topCarResults.items.filter((car) => car.id !== id),
        }
      : state.topCarResults,

    carMapResults: state.carMapResults
      ? {
          ...state.carMapResults,
          items: state.carMapResults.items.filter((car) => car.id !== id),
        }
      : state.carMapResults,
  })),

  on(removeCarFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(searchCarReports, (state, { reportQuery }) => ({
    ...state,
    reportQuery,
    loading: true,
    error: null,
  })),

  on(searchCarReportsSuccess, (state, { carReports: carReports }) => ({
    ...state,
    carReports: carReports,
    loading: false,
  })),

  on(searchCarReportsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(addCarReportSuccess, (state, { report }) => ({
    ...state,
    loading: false,
    carReports: state.carReports ? [...state.carReports, report] : [report],
  })),

  on(addCarFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  on(updateCarReportSuccess, (state, { report }) => ({
    ...state,
    loading: false,
    carReports: state.carReports?.map((c) => (c.id === report.id ? report : c)),
  })),

  on(updateCarReportFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(setCarMapView, (state, action) => {
    const isMap = action.isCarMapView;
    if (isMap === state.isCarMapView) return state;
    const { changed, filters } = sharedModeFilters(state, state.isCarMapView, isMap);
    return { ...state, ...filters, isCarMapView: isMap, historyRestored: false,
      carResults: !isMap && changed ? undefined : state.carResults,
      carMapResults: isMap && changed ? undefined : state.carMapResults,
      listLoading: !isMap && changed ? false : state.listLoading,
      mapLoading: isMap && changed ? false : state.mapLoading,
    };
  }),
  on(toggleCarMapView, state => {
    const isMap = !state.isCarMapView;
    const { changed, filters } = sharedModeFilters(state, state.isCarMapView, isMap);
    return { ...state, ...filters, isCarMapView: isMap, historyRestored: false,
      carResults: !isMap && changed ? undefined : state.carResults,
      carMapResults: isMap && changed ? undefined : state.carMapResults,
    };
  }),
  // Initial List loading must not invalidate explicit Map results.
  on(ensureCarList, (state) =>
    state.historyRestored || state.isCarMapView || state.carResults
      ? state
      : {
          ...state,
          query: state.query ?? structuredClone(state.listDraft),
          listLoading: true,
          listError: null,
        }
  ),
  on(clearCarPolygon, (state) => ({
    ...state,
    activePolygon: null,
    polygonRevision: state.polygonRevision + 1,
    mapQuery: state.mapQuery ? withoutViewport(state.mapQuery) : undefined,
    carMapResults: undefined,
    mapLoading: false,
    mapError: null,
  })),
  on(changeCarPage, changeCarPageSize, (state) =>
    state.isCarMapView
      ? state
      : { ...state, listLoading: true, listError: null, carResults: undefined }
  ),
  on(retryCarSearch, (state) => ({
    ...state,
    ...(state.isCarMapView
      ? { mapLoading: true, mapError: null, carMapResults: undefined }
      : { listLoading: true, listError: null, carResults: undefined }),
  }))
);
